import React, { useEffect, useRef, useState } from 'react';
import { debounce, isEmpty } from 'lodash';
import { connect } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import {
  Heading,
  Field,
  FormRow,
  InputGroup,
  InputGroupAddon,
  Spacer,
  Button,
  HelpIcon,
  Flex,
  Text,
  Message,
} from '@oliasoft-open-source/react-ui-library';
import { generatePath } from 'store/navigation/navigation';
import { routes } from 'routes/routes';
import { getAllRigOperationTemplates } from 'src/store/entities/company-settings/company-settings';
import { getResolver } from 'src/validation/resolver';
import translations from 'src/internationalisation/translation-map.json';
import { tasksSchemaValidator } from 'schemas/ajv-validators';
import {
  Input,
  Select,
  UnitInput,
  TextArea,
  NumberInput,
  Toggle,
  UnitInputConvertable,
} from 'client/common/form-inputs';
import { estimateTypes, distributionTypes } from 'src/enums/tasks';
import { addTask, initialTask, getPreviewTimesFromOffsetWells } from 'store/entities/activity-model/activity-model';
import { convertInputUnits, convertToInputUnits, convertToStorageUnits } from 'client/common/units/units';
import { useAutoSave } from 'client/common/auto-save/use-auto-save';
import { isActivityWithSubs, selectExtendedLinkFields } from 'store/entities/activity-model/selectors';
import { BranchesTable } from 'views/activity-model/tasks/branches-table';
import { autoSaveWaitShort } from 'src/config/config';
import { selectSectionsDataStructure } from 'src/store/entities/sections-data-structure/selector';
import { withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import { selectUnitByQuantity } from 'store/entities/unit-templates/selectors';
import { QuantitiesTypes } from 'src/enums/unit-templates';
import { convertToKebabCase } from 'client/common/utils/string-utils.ts';
import styles from './task.module.less';

const TaskForm = ({
  task,
  addTask,
  projectId,
  estimateTypesList,
  operation,
  depthUnit,
  ROPUnit,
  durationUnit,
  linkFields,
  isPageDisabled,
  distributionTypesList,
  isRisk,
  showBranch,
  onClickDeleteTask,
  operationActivities,
  isFetching,
  getPreviewTimesFromOffsetWells,
  isFetchingPreviewTimes,
  rigOperationTemplates,
  getAllRigOperationTemplates,
  handleOnClickGetTimeFromOffsetWells,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { company } = useParams();
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
    setValue,
    getValues,
  } = useForm({
    mode: 'onBlur',
    defaultValues: task || initialTask,
    shouldFocusError: false,
    resolver: getResolver(tasksSchemaValidator),
  });
  const [isEditing, setIsEditing] = useState(true);

  const [
    estimateType,
    distributionType,
    withBranch,
    sectionsOperationActivityId,
    depthToLink,
    depthFromLink,
    rigTemplateActivityId,
    isLinked,
    isImported,
  ] = watch([
    'estimateType',
    'distribution',
    'withBranch',
    'sectionsOperationActivityId',
    'depthToLink',
    'depthFromLink',
    'rigTemplateActivityId',
    'isLinked',
    'isImported',
  ]);
  const isOperationSpeed = estimateType === estimateTypes.OPERATION_SPEED;

  const isImportedFromTemplate = !!rigTemplateActivityId || !!isImported;
  const isLinkedToTemplate = isImportedFromTemplate && isLinked;
  const isItemDeletedFromTemplate = isLinked && !rigTemplateActivityId;
  const linkingMessage = isItemDeletedFromTemplate
    ? translations.activityModel_activityIsDeletedFromTemplate
    : translations.activityModel_activityIsLinkedToTemplate;
  const linkedTemplate = rigOperationTemplates.find((item) =>
    item.activities?.some((activity) => activity.rigOperationTemplateActivityId === rigTemplateActivityId),
  );

  const getSelectedTemplateData = (field = '') => {
    const templateIdx = rigOperationTemplates.findIndex((template) =>
      template.activities.some((activity) => activity.rigOperationTemplateActivityId === rigTemplateActivityId),
    );
    if (templateIdx >= 0) {
      const template = rigOperationTemplates[templateIdx];
      const activity = template.activities.find(
        (activity) => activity.rigOperationTemplateActivityId === rigTemplateActivityId,
      );
      if (activity) {
        return field
          ? [
              {
                label: `${template.name}: ${activity.name}`,
                value: activity?.[field] ?? 0,
              },
            ]
          : `${template.name}: ${activity.name}`;
      }
    }
  };

  useEffect(() => {
    getAllRigOperationTemplates();
  }, []);

  const timeUnits = isOperationSpeed ? ROPUnit : durationUnit;

  useEffect(() => {
    const taskData = task || initialTask;
    const convertedLength = convertToInputUnits(taskData, ['from', 'to'], 'm', depthUnit, true);
    const convertedTask = convertInputUnits(
      convertedLength,
      ['min', 'most', 'max'],
      isOperationSpeed ? 'm/h' : 'h',
      isOperationSpeed ? ROPUnit : durationUnit,
      true,
    );

    reset(convertedTask);
  }, [task?.taskId, reset]);

  const debounceAddTask = useRef(debounce(addTask, autoSaveWaitShort));

  const onSubmit = handleSubmit((data) => {
    const convertedLength = convertToStorageUnits(data, ['from', 'to'], {
      from: 'm',
      to: 'm',
    });
    const convertedTask = convertInputUnits(
      convertedLength,
      ['min', 'most', 'max'],
      isOperationSpeed ? ROPUnit : durationUnit,
      isOperationSpeed ? 'm/h' : 'h',
    );
    convertedTask.operationId = operation.operationId;
    convertedTask.projectId = projectId;
    convertedTask.isApplyFromOffset = false;

    debounceAddTask.current(convertedTask);
  });

  useAutoSave(onSubmit, watch);

  const distributionFieldMap = {
    [distributionTypes.PERT]: [
      { label: t(translations.minimum), name: 'min' },
      { label: t(translations.mostLikely), name: 'most' },
      { label: t(translations.maximum), name: 'max' },
    ],
    [distributionTypes.UNIFORM]: [
      { label: t(translations.minimum), name: 'min' },
      { label: t(translations.maximum), name: 'max' },
    ],
    [distributionTypes.SPIKE]: [{ label: t(translations.mostLikely), name: 'most' }],
  };

  const distributionFieldOptions = {
    min: [
      {
        label: 'Offset Minimum',
        value: task?.offsetWellsTimeEstimate?.distribution?.A ?? 0,
      },
    ],
    most: [
      {
        label: 'Offset Average',
        value: task?.offsetWellsTimeEstimate?.distribution?.B ?? 0,
      },
    ],
    max: [
      {
        label: 'Offset Maximum',
        value: task?.offsetWellsTimeEstimate?.distribution?.C ?? 0,
      },
    ],
  };

  useEffect(() => {
    if (!isEmpty(task?.offsetWellsTimeEstimate) && task?.isApplyFromOffset) {
      const { min, most, max } = distributionFieldOptions;
      const taskWithTimeFromOffset = {
        ...task,
        min: min[0].value,
        most: most[0].value,
        max: max[0].value,
        distribution: task?.offsetWellsTimeEstimate?.distribution?.type ?? distributionTypes.PERT,
        estimateType: task?.offsetWellsTimeEstimate?.estimateType ?? estimateTypes.OPERATION_TIME,
      };

      const convertedTask = convertToInputUnits(taskWithTimeFromOffset, ['from', 'to'], 'm', depthUnit, true);

      reset(convertedTask);
    }
  }, [reset, task]);

  useEffect(() => {
    if (sectionsOperationActivityId && !isEditing) {
      const selectedOption = operationActivities.find((option) => option.value === sectionsOperationActivityId);
      const isDefaultName = getValues('name').toLowerCase() === t(translations.activityModel_newActivity).toLowerCase();
      if (getValues('name') === '' || isDefaultName) {
        setValue('name', selectedOption.label);
      }
      setIsEditing(true);
    }
  }, [sectionsOperationActivityId]);

  const handleCategoryChange = (ev) => {
    setIsEditing(false);
    setValue('sectionsOperationActivityId', ev.target.value);
  };

  const operationActivity = operationActivities.find((item) => item.value === sectionsOperationActivityId);

  const isDrill = operationActivity?.label === 'Drill';

  const handleApplyClick = () => {
    const taskWithFlag = {
      ...task,
      isDrill,
    };
    handleOnClickGetTimeFromOffsetWells(projectId, taskWithFlag, operation);
    setValue('rigTemplateActivityId', null);
  };

  return (
    <>
      <Flex justifyContent="space-between" alignItems="flex-start">
        <Heading top>{watch('name')}</Heading>
        <Field>
          <Button
            colored="danger"
            icon="delete"
            label={t(translations.activityModel_deleteActivity)}
            onClick={() => onClickDeleteTask(task)}
            disabled={isPageDisabled}
            small
          />
        </Field>
      </Flex>
      {isImportedFromTemplate && (
        <Message
          message={{
            content: (
              <Flex justifyContent={'space-between'} gap="var(--padding)" alignItems="center">
                <div style={{ flex: 1 }}>
                  <Text>
                    {t(isLinkedToTemplate ? linkingMessage : translations.activityModel_activityIsImportedFromTemplate)}
                  </Text>
                </div>
                {!isItemDeletedFromTemplate && isLinkedToTemplate && (
                  <span style={{ flexShrink: 0 }}>
                    <Button
                      small
                      label={t(translations.activityModel_viewTemplate)}
                      onClick={() => {
                        const path = generatePath(
                          routes.companySettings.nested.rigOperationTemplates,
                          { company },
                          true,
                        );
                        const navigateState = linkedTemplate
                          ? {
                              templateId: linkedTemplate.rigOperationTemplateId,
                            }
                          : null;
                        navigate(path, {
                          state: navigateState,
                        });
                      }}
                    />
                  </span>
                )}
              </Flex>
            ),
            type: 'Info',
            visible: true,
          }}
        />
      )}
      <Spacer />
      <form className={styles.taskForm}>
        <FormRow>
          {!isImportedFromTemplate && (
            <Field>
              <Button
                testId={`task-form-button-${convertToKebabCase(
                  t(translations.activityModel_applyAllTimesFromOffsetWells),
                )}`}
                loading={isFetching}
                disabled={!task?.sectionsOperationActivityId || isPageDisabled}
                label={t(translations.activityModel_applyAllTimesFromOffsetWells)}
                onClick={handleApplyClick}
              />
              <Spacer width="var(--padding-sm)" />
              <Button
                basic
                testId={`task-form-button-${convertToKebabCase(t(translations.preview))}`}
                loading={isFetchingPreviewTimes}
                disabled={!task?.sectionsOperationActivityId || isPageDisabled}
                label={t(translations.preview)}
                onClick={() => getPreviewTimesFromOffsetWells(projectId, sectionsOperationActivityId, isDrill)}
              />
            </Field>
          )}
        </FormRow>
        <FormRow>
          {!isRisk && (
            <Field label={t(translations.activity)}>
              <Select
                testId="task-form-select-activity"
                name="sectionsOperationActivityId"
                control={control}
                errors={errors}
                width={250}
                options={operationActivities}
                onChange={handleCategoryChange}
                placeholder={t(translations.activityModel_selectActivity)}
                disabled={isPageDisabled}
              />
            </Field>
          )}
          <Field label={isRisk ? 'Risk name' : t(translations.name)}>
            <Input
              testId="operation-form-input-name"
              name="name"
              control={control}
              errors={errors}
              disabled={isPageDisabled || isImportedFromTemplate}
              width={250}
            />
          </Field>
        </FormRow>
        <FormRow>
          <Field label={t(translations.probability)}>
            <InputGroup width="120px">
              <NumberInput
                testId="task-form-input-probability"
                name="certainty"
                control={control}
                errors={errors}
                disabled={isPageDisabled}
              />
              <InputGroupAddon>%</InputGroupAddon>
            </InputGroup>
          </Field>
        </FormRow>
        <FormRow>
          <Field label={t(translations.estimateType)}>
            <Select
              testId="task-form-select-estimate-type"
              name="estimateType"
              control={control}
              errors={errors}
              options={estimateTypesList}
              width="auto"
              disabled={isPageDisabled || isImportedFromTemplate}
            />
          </Field>
          <Field label={t(translations.activityModel_distributionType)}>
            <Select
              testId="task-form-select-distribution-type"
              name="distribution"
              control={control}
              errors={errors}
              options={distributionTypesList}
              width="auto"
              disabled={isPageDisabled || isImportedFromTemplate}
            />
          </Field>
        </FormRow>
        {!!isRisk && showBranch && (
          <>
            <Toggle name="withBranch" label="Branch risk" control={control} disabled={isPageDisabled} noMargin />
            {withBranch && (
              <>
                <Spacer />
                <BranchesTable
                  operationId={operation.operationId}
                  projectId={projectId}
                  riskId={task.taskId}
                  estimateType={estimateType}
                  distributionType={distributionType}
                  onClickDeleteTask={onClickDeleteTask}
                  debounceAddTask={debounceAddTask}
                  isPageDisabled={isPageDisabled}
                />
              </>
            )}
            <Spacer />
          </>
        )}
        {!withBranch && (
          <>
            {estimateType === estimateTypes.OPERATION_SPEED && (
              <>
                <Heading>{t(translations.depth)}</Heading>
                <Spacer />
                <FormRow>
                  <Field label={t(translations.from)}>
                    <UnitInputConvertable
                      name="from"
                      unitkey={QuantitiesTypes.Depth}
                      control={control}
                      errors={errors}
                      predefinedOptions={linkFields}
                      selectedPredefinedOptionKey={depthFromLink}
                      onPredefinedChange={setValue}
                      disabled={isPageDisabled}
                      unit={depthUnit}
                      unitTemplate={{ [QuantitiesTypes.Depth]: depthUnit }}
                    />
                  </Field>
                  <Field label={t(translations.to)}>
                    <UnitInputConvertable
                      name="to"
                      unitkey={QuantitiesTypes.Depth}
                      control={control}
                      errors={errors}
                      predefinedOptions={linkFields}
                      selectedPredefinedOptionKey={depthToLink}
                      onPredefinedChange={setValue}
                      disabled={isPageDisabled}
                      unit={depthUnit}
                      unitTemplate={{ [QuantitiesTypes.Depth]: depthUnit }}
                    />
                  </Field>
                </FormRow>
              </>
            )}
            <Spacer />
            <Heading>
              {estimateType === estimateTypes.OPERATION_SPEED
                ? t(translations.activityModel_operationSpeed)
                : t(translations.time)}
            </Heading>
            <FormRow>
              {distributionFieldMap[distributionType]?.map((field) => (
                <Field label={field.label} key={field.name}>
                  <UnitInput
                    testId={`task-form-${convertToKebabCase(field.name)}-input`}
                    name={field.name}
                    unit={timeUnits}
                    control={control}
                    errors={errors}
                    disabled={isPageDisabled || isImportedFromTemplate}
                    predefinedOptions={
                      rigTemplateActivityId ? getSelectedTemplateData(field.name) : distributionFieldOptions[field.name]
                    }
                    width="142px"
                  />
                </Field>
              ))}
            </FormRow>
          </>
        )}
        <Field label={t(translations.comments)}>
          <TextArea name="comments" control={control} errors={errors} rows={5} disabled={isPageDisabled} />
        </Field>
      </form>
    </>
  );
};

const mapStateToProps = ({ entities }, ownProps) => {
  const {
    activityModel: { operations, isFethcing, isFetchingPreviewTimes },
    companySettings: { rigOperationTemplates },
  } = entities;

  const { activities } = selectSectionsDataStructure(entities);

  const activeOperation = operations.filter((operation) => operation.active)[0];

  const operationActivities = activities
    .filter((activity) => activeOperation?.sectionsOperationId === activity?.sectionsOperationId)
    .map((activity) => ({
      label: activity.name,
      value: activity.sectionsOperationActivityId,
    }));

  return {
    estimateTypesList: [
      { label: 'Operation time', value: estimateTypes.OPERATION_TIME },
      { label: 'Operation speed', value: estimateTypes.OPERATION_SPEED },
    ],
    distributionTypesList: [
      {
        label: 'Spike',
        value: distributionTypes.SPIKE,
        icon: <HelpIcon text={i18n.t(translations.activityModel_spikeDistributionExplanation)} />,
      },
      {
        label: 'Uniform',
        value: distributionTypes.UNIFORM,
        icon: <HelpIcon text={i18n.t(translations.activityModel_uniformDistributionExplanation)} />,
      },
      {
        label: 'Pert',
        value: distributionTypes.PERT,
        icon: <HelpIcon text={i18n.t(translations.activityModel_pertDistributionExplanation)} />,
      },
    ],
    depthUnit: selectUnitByQuantity(entities, QuantitiesTypes.Depth),
    ROPUnit: selectUnitByQuantity(entities, QuantitiesTypes.ROP),
    durationUnit: selectUnitByQuantity(entities, QuantitiesTypes.Duration),
    linkFields: selectExtendedLinkFields({ entities }),
    isRisk: ownProps.task?.parentId,
    showBranch: !isActivityWithSubs({ entities }, ownProps.operation?.operationId, ownProps.task?.taskId),
    operationActivities,
    isFethcing,
    isFetchingPreviewTimes,
    rigOperationTemplates,
  };
};

const mapDispatchToProps = {
  addTask,
  getPreviewTimesFromOffsetWells,
  getAllRigOperationTemplates,
};

const Container = withErrorBoundary(connect(mapStateToProps, mapDispatchToProps)(TaskForm));

export { Container as TaskForm };
